/**
 * POSTeam - Team Management
 *
 * View and manage property staff (owner access only).
 *
 * URL: pos.cleanmybed.com/{slug}/team
 */

import { useState, useEffect } from 'react';
import { POS_GOLD } from '../../../config/posRoomStatus';
import { useParams } from 'react-router-dom';
import { formatDate } from '../../../utils/formatters';
import { useAuth } from '../../../contexts/AuthContext';
import '../pos-theme.css';

interface TeamMemberUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

interface TeamMember {
  id: string;
  access_level: string;
  created_at: string;
  users: TeamMemberUser | null;
}

interface PendingInvite {
  id: string;
  email: string;
  access_level: string;
  expires_at: string;
}

interface InviteResponse {
  success?: boolean;
  error?: string;
  invite?: PendingInvite;
}

function POSTeam() {
  const { propertySlug } = useParams();
  const { user, supabase } = useAuth();

  const [team, setTeam] = useState<TeamMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [accessLevel, setAccessLevel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteAccessLevel, setInviteAccessLevel] = useState('manager');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editAccessLevel, setEditAccessLevel] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingMember, setDeletingMember] = useState<TeamMember | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    async function fetchTeamData() {
      try {
        // Get property by slug
        const { data: properties } = await supabase!
          .from('pos_user_properties')
          .select('property_id, access_level')
          .eq('slug', propertySlug)
          .single();

        if (!properties) {
          setError('Property not found');
          setLoading(false);
          return;
        }

        setPropertyId(properties.property_id);
        setAccessLevel(properties.access_level);

        // Fetch team members
        const { data: managers, error: managerError } = await supabase!
          .from('hosp_property_managers')
          .select(`
            id,
            access_level,
            created_at,
            users (id, email, first_name, last_name)
          `)
          .eq('property_id', properties.property_id);

        if (managerError) throw managerError;

        setTeam((managers as unknown as TeamMember[]) || []);

        // If owner, fetch pending invites too
        if (properties.access_level === 'owner') {
          const { data: invites } = await supabase!
            .from('pos_invites')
            .select('*')
            .eq('property_id', properties.property_id)
            .is('accepted_at', null)
            .gt('expires_at', new Date().toISOString());

          setPendingInvites((invites as PendingInvite[]) || []);
        }
      } catch (_err: unknown) {
        setError('Failed to load team');
      } finally {
        setLoading(false);
      }
    }

    fetchTeamData();
  }, [propertySlug, supabase]);

  async function handleInvite() {
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setInviting(true);
    setInviteError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pos-invite-manager`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase!.auth.getSession()).data.session?.access_token}`
          },
          body: JSON.stringify({
            email: inviteEmail.trim(),
            property_id: propertyId,
            access_level: inviteAccessLevel
          })
        }
      );

      const data: InviteResponse = await response.json();

      if (!response.ok || !data.success) {
        setInviteError(data.error || 'Failed to send invite');
        setInviting(false);
        return;
      }

      // Add to pending invites
      if (data.invite) {
        setPendingInvites(prev => [...prev, data.invite!]);
      }

      // Reset form
      setInviteEmail('');
      setInviteAccessLevel('manager');
      setShowInviteModal(false);
      showToast('Invitation sent successfully', 'success');
    } catch (_err: unknown) {
      setInviteError('Failed to send invite');
    } finally {
      setInviting(false);
    }
  }

  function openEditModal(member: TeamMember) {
    setEditingMember(member);
    setEditAccessLevel(member.access_level);
    setEditError('');
    setShowEditModal(true);
  }

  async function handleUpdateAccess() {
    if (!editingMember || !editAccessLevel) return;

    setSaving(true);
    setEditError('');

    try {
      const { error: updateError } = await supabase!
        .from('hosp_property_managers')
        .update({ access_level: editAccessLevel })
        .eq('id', editingMember.id);

      if (updateError) throw updateError;

      // Update local state
      setTeam(prev => prev.map(m =>
        m.id === editingMember.id
          ? { ...m, access_level: editAccessLevel }
          : m
      ));

      setShowEditModal(false);
      setEditingMember(null);
      showToast('Access level updated', 'success');
    } catch (_err: unknown) {
      setEditError('Failed to update access level');
    } finally {
      setSaving(false);
    }
  }

  function openDeleteModal(member: TeamMember) {
    setDeletingMember(member);
    setShowDeleteModal(true);
  }

  async function handleRemoveMember() {
    if (!deletingMember) return;

    setDeleting(true);

    try {
      const { error: deleteError } = await supabase!
        .from('hosp_property_managers')
        .delete()
        .eq('id', deletingMember.id);

      if (deleteError) throw deleteError;

      // Remove from local state
      setTeam(prev => prev.filter(m => m.id !== deletingMember.id));

      setShowDeleteModal(false);
      setDeletingMember(null);
      showToast('Member removed', 'success');
    } catch (_err: unknown) {
      showToast('Failed to remove member', 'error');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="pos-loading">
        <div className="pos-loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pos-card">
        <p style={{ color: 'var(--color-error)' }}>{error}</p>
      </div>
    );
  }

  const isOwner = accessLevel === 'owner';

  return (
    <div className="pos-team">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, padding: '12px 20px', zIndex: 1000,
          borderRadius: 'var(--radius)', fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          background: toast.type === 'error' ? 'var(--color-error-bg)' : toast.type === 'success' ? 'var(--color-success-bg)' : 'var(--color-info-bg)',
          color: toast.type === 'error' ? 'var(--color-error)' : toast.type === 'success' ? 'var(--color-success)' : 'var(--color-info)',
        }}>
          {toast.message}
        </div>
      )}

      {/* Team Members */}
      <div className="pos-card">
        <div className="flex items-center justify-between mb-lg">
          <h3 className="pos-card-title m-0">Team Members</h3>
          {isOwner && (
            <button
              className="pos-btn pos-btn-primary"
              onClick={() => { setInviteError(''); setShowInviteModal(true); }}
            >
              Invite Member
            </button>
          )}
        </div>

        {team.length === 0 ? (
          <div className="pos-empty-state">
            <div className="pos-empty-state-icon">👥</div>
            <p className="pos-empty-state-title">No Team Members</p>
            <p className="pos-empty-state-text">
              You&apos;re the only one with access to this property.
            </p>
          </div>
        ) : (
          <div className="pos-table-container">
            <table className="pos-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Access</th>
                  <th>Added</th>
                  {isOwner && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {team.map(member => {
                  const isSelf = member.users?.id === user?.id;
                  return (
                    <tr key={member.id}>
                      <td>
                        <strong>
                          {member.users?.first_name} {member.users?.last_name}
                        </strong>
                        {isSelf && (
                          <span style={{ color: POS_GOLD, marginLeft: '8px' }}>(You)</span>
                        )}
                      </td>
                      <td>{member.users?.email}</td>
                      <td>
                        <span className="pos-badge pos-badge-gold">
                          {member.access_level}
                        </span>
                      </td>
                      <td>{formatDate(member.created_at)}</td>
                      {isOwner && (
                        <td>
                          {isSelf ? (
                            <span style={{ color: 'var(--color-text-light)', fontSize: 'var(--text-sm)' }}>—</span>
                          ) : (
                            <div className="flex gap-sm">
                              <button
                                className="pos-btn-icon"
                                onClick={() => openEditModal(member)}
                                title="Edit access level"
                              >
                                ✏️
                              </button>
                              <button
                                className="pos-btn-icon pos-btn-danger"
                                onClick={() => openDeleteModal(member)}
                                title="Remove from property"
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Invites (Owner only) */}
      {isOwner && pendingInvites.length > 0 && (
        <div className="pos-card mt-xl">
          <h3 className="pos-card-title">Pending Invitations</h3>
          <div className="pos-table-container">
            <table className="pos-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Access</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map(invite => (
                  <tr key={invite.id}>
                    <td>{invite.email}</td>
                    <td>
                      <span className="pos-badge pos-badge-pending">
                        {invite.access_level}
                      </span>
                    </td>
                    <td>{formatDate(invite.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Access Modal */}
      {showEditModal && editingMember && (
        <div className="pos-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="pos-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Access Level</h3>

            <p style={{ marginBottom: '16px', color: 'var(--color-sidebar-hover)' }}>
              Changing access for <strong>{editingMember.users?.first_name} {editingMember.users?.last_name}</strong>
            </p>

            {editError && (
              <div style={{
                padding: '12px',
                background: 'rgba(200, 50, 50, 0.1)',
                border: '1px solid rgba(200, 50, 50, 0.2)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-error)',
                marginBottom: '16px'
              }}>
                {editError}
              </div>
            )}

            <div className="mb-xl">
              <label className="pos-label">Access Level</label>
              <select
                className="pos-input"
                value={editAccessLevel}
                onChange={e => setEditAccessLevel(e.target.value)}
              >
                <option value="owner">Owner - Full control</option>
                <option value="manager">Manager - Manage rooms & services</option>
                <option value="viewer">Viewer - Read only</option>
              </select>
            </div>

            <div className="flex justify-end gap-md">
              <button
                className="pos-btn pos-btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="pos-btn pos-btn-primary"
                onClick={handleUpdateAccess}
                disabled={saving || editAccessLevel === editingMember.access_level}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingMember && (
        <div className="pos-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="pos-modal" onClick={e => e.stopPropagation()}>
            <h3>Remove Team Member</h3>

            <p style={{ marginBottom: '24px', color: 'var(--color-sidebar-hover)' }}>
              Are you sure you want to remove <strong>{deletingMember.users?.first_name} {deletingMember.users?.last_name}</strong> from this property?
            </p>

            <p style={{ marginBottom: '24px', color: 'var(--color-text-light)', fontSize: 'var(--text-base)' }}>
              They will lose access immediately and would need a new invite to regain access.
            </p>

            <div className="flex justify-end gap-md">
              <button
                className="pos-btn pos-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="pos-btn"
                style={{ background: 'var(--color-error)', color: 'var(--color-white)' }}
                onClick={handleRemoveMember}
                disabled={deleting}
              >
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="pos-modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="pos-modal" onClick={e => e.stopPropagation()}>
            <h3>Invite Team Member</h3>

            {inviteError && (
              <div style={{
                padding: '12px',
                background: 'rgba(200, 50, 50, 0.1)',
                border: '1px solid rgba(200, 50, 50, 0.2)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-error)',
                marginBottom: '16px'
              }}>
                {inviteError}
              </div>
            )}

            <div className="mb-lg">
              <label className="pos-label">Email</label>
              <input
                type="email"
                className="pos-input"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={e => {
                  setInviteEmail(e.target.value);
                  setInviteError('');
                }}
              />
            </div>

            <div className="mb-xl">
              <label className="pos-label">Access Level</label>
              <select
                className="pos-input"
                value={inviteAccessLevel}
                onChange={e => setInviteAccessLevel(e.target.value)}
              >
                <option value="viewer">Viewer - Read only</option>
                <option value="manager">Manager - Manage rooms & services</option>
              </select>
            </div>

            <div className="flex justify-end gap-md">
              <button
                className="pos-btn pos-btn-secondary"
                onClick={() => setShowInviteModal(false)}
                disabled={inviting}
              >
                Cancel
              </button>
              <button
                className="pos-btn pos-btn-primary"
                onClick={handleInvite}
                disabled={inviting}
              >
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Styles */}
      <style>{`
        .pos-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .pos-modal {
          background: #EAE7E1;
          border-radius: 12px;
          padding: 32px;
          width: 100%;
          max-width: 400px;
        }

        .pos-modal h3 {
          margin: 0 0 24px 0;
          font-size: 20px;
          font-weight: 600;
          color: #000;
        }

        .pos-btn-icon {
          background: transparent;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 6px 10px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .pos-btn-icon:hover {
          background: #f5f5f5;
          border-color: #ccc;
        }

        .pos-btn-icon.pos-btn-danger:hover {
          background: rgba(200, 50, 50, 0.1);
          border-color: #c83232;
        }
      `}</style>
    </div>
  );
}

export default POSTeam;
